import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MapPinIcon, CalendarIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { getUserBookingsDetails } from "../../redux/reducers/Properties/PropertiesSlice";
import { AppDispatch, RootState } from "redux/store";
import { currencySymbol } from "shared/CurrencySymbol";
import CommonLayout from "./CommonLayout";
import Pagination from "shared/Pagination/Pagination";
import SEO from "components/SEO/SEO";
import { Link } from "react-router-dom";

const FMT = "MMMM DD, YYYY";

interface BookingData {
  id: string;
  [key: string]: any;
}

const money = (v: any) => `${currencySymbol()} ${Number(v || 0).toFixed(2)}`;

const statusStyle = (status: string) => {
  switch (status) {
    case "confirmed":
    case "completed":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    case "canceled":
    case "cancelled":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-gray-300";
  }
};

const AccountBookings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [page, setPage] = useState(1);
  const [bookings, setBookings] = useState<BookingData[]>([]);
  const [meta, setMeta] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchBookings = useCallback(
    async (pageNum: number) => {
      setIsLoading(true);
      try {
        const res = await dispatch(getUserBookingsDetails({ page: pageNum })).unwrap();
        setBookings(Array.isArray(res?.bookings) ? res.bookings : []);
        setMeta(res?.meta || {});
      } catch {
        setBookings([]);
        setMeta({});
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    fetchBookings(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const today = moment().startOf("day");
  const parse = (d: string) => moment(d, FMT);

  const upcomingCount = bookings.filter((b) => parse(b?.check_in_date).isAfter(today)).length;
  const currentStay = bookings.find(
    (b) =>
      parse(b?.check_in_date).isSameOrBefore(today) &&
      parse(b?.check_out_date).isSameOrAfter(today)
  );

  const rangeFrom = meta?.total_count ? (page - 1) * 10 + 1 : 0;
  const rangeTo = meta?.total_count ? Math.min(page * 10, meta.total_count) : 0;

  return (
    <div className="nc-AccountBookings">
      <SEO title="My Bookings | Nomadroof" description="View your booking history and current stays." noIndex={true} />
      <CommonLayout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              My Bookings
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              View your confirmed stays and booking history
            </p>
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 px-4 py-3 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 m-0">Total bookings</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white m-0">
                {meta?.total_count ?? "—"}
              </p>
            </div>
            <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 px-4 py-3 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 m-0">Upcoming</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white m-0">
                {upcomingCount}
              </p>
            </div>
            <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 px-4 py-3 text-center col-span-2 sm:col-span-1">
              <p className="text-xs text-gray-500 dark:text-gray-400 m-0">Currently staying</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white m-0 truncate">
                {currentStay ? currentStay?.property?.title : "—"}
              </p>
            </div>
          </div>

          {meta?.total_count > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Showing {rangeFrom}–{rangeTo} of {meta.total_count} bookings
            </p>
          )}

          {/* List */}
          <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
            {isLoading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 bg-gray-100 dark:bg-neutral-800 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : bookings.length > 0 ? (
              <div className="divide-y divide-gray-100 dark:divide-neutral-800">
                {bookings.map((b) => {
                  const isOpen = expandedId === b.id;
                  return (
                    <div key={b.id}>
                      {/* Row — click to expand */}
                      <button
                        type="button"
                        onClick={() => setExpandedId(isOpen ? null : b.id)}
                        className="w-full text-left flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-neutral-800/50 transition-colors"
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200 dark:bg-neutral-700">
                          {Array.isArray(b?.property?.place_images) && b.property.place_images[0] ? (
                            <img
                              src={b.property.place_images[0]}
                              alt={b?.property?.title || "Property"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <MapPinIcon className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate m-0">
                            {b?.property?.title}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-0">
                            {moment(b?.check_in_date, FMT).format("MMM DD, YYYY")} –{" "}
                            {moment(b?.check_out_date, FMT).format("MMM DD, YYYY")}
                            {b?.is_shared_property && b?.booked_room ? ` · ${b.booked_room}` : ""}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-0">
                            {b?.number_of_guests} guest{b?.number_of_guests !== 1 ? "s" : ""}
                            {b?.number ? ` · #${b.number}` : ""}
                          </p>
                        </div>

                        <div className="text-right flex-shrink-0 flex items-center gap-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white m-0">
                              {money(b?.total_amount)}
                            </p>
                            <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle(b?.status)}`}>
                              {String(b?.status || "Confirmed").replace(/^\w/, (c: string) => c.toUpperCase())}
                            </span>
                          </div>
                          <ChevronDownIcon
                            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                          />
                        </div>
                      </button>

                      {/* Expanded detail — built entirely from data already in state, no extra fetch */}
                      {isOpen && (
                        <div className="px-4 pb-5 pt-1 bg-gray-50 dark:bg-neutral-800/40">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Check in</p>
                              <p className="text-sm text-gray-900 dark:text-white m-0">{b.check_in_date}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Check out</p>
                              <p className="text-sm text-gray-900 dark:text-white m-0">{b.check_out_date}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Duration</p>
                              <p className="text-sm text-gray-900 dark:text-white m-0">
                                {b.no_of_months > 0 ? `${b.no_of_months} month${b.no_of_months !== 1 ? "s" : ""}` : "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                              <p className="text-sm text-gray-900 dark:text-white m-0">{b.number_of_guests || "—"}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Address</p>
                              <p className="text-sm text-gray-900 dark:text-white m-0">
                                {b?.property?.address || "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Room</p>
                              <p className="text-sm text-gray-900 dark:text-white m-0">
                                {b?.is_shared_property ? (b?.booked_room || "—") : "Entire property"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Invoice</p>
                              <p className="text-sm text-gray-900 dark:text-white m-0">
                                {b?.invoice_no ? `INV-${b.invoice_no}` : "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Paid on</p>
                              <p className="text-sm text-gray-900 dark:text-white m-0">{b?.paid_at || "—"}</p>
                            </div>
                          </div>

                          <div className="flex justify-between text-sm pt-3 border-t border-gray-200 dark:border-neutral-700">
                            <span className="text-gray-500 dark:text-gray-400">Item total</span>
                            <span className="text-gray-900 dark:text-white">{money(b.item_total)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-semibold pt-1">
                            <span className="text-gray-900 dark:text-white">Total</span>
                            <span className="text-gray-900 dark:text-white">{money(b.total_amount)}</span>
                          </div>

                          <div className="mt-4">
                            <Link
                              to={`/booking_request?id=${b.id}`}
                              className="text-sm font-medium text-primary-500 hover:underline"
                            >
                              Open full booking page →
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-14 px-6">
                <CalendarIcon className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-base font-medium text-gray-900 dark:text-white">No bookings yet</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
                  Your confirmed stays will show up here.
                </p>
                <Link
                  to="/properties"
                  className="inline-block text-sm font-medium text-primary-500 hover:underline"
                >
                  Browse properties
                </Link>
              </div>
            )}
          </div>

          {meta?.total_pages > 1 && (
            <div className="flex justify-center mt-6">
              <Pagination
                currentPage={page}
                totalPages={meta?.total_pages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </CommonLayout>
    </div>
  );
};

export default AccountBookings;